import { DevicePlatform, hasPermission, type MeResponse, type Permission } from '@i9portal/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthContextValue, AuthStatus } from '../../types/auth';
import { api } from '../api/client';
import { authApi } from '../api/endpoints/auth';
import { AuthContext } from './auth-context';

function browserDeviceName(): string {
  if (typeof navigator === 'undefined') return 'Navegador';
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Navegador';
  const os = /Windows/.test(ua)
    ? 'Windows'
    : /Android/.test(ua)
      ? 'Android'
      : /iPhone|iPad/.test(ua)
        ? 'iOS'
        : /Mac OS/.test(ua)
          ? 'macOS'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  return `${browser}${os ? ` (${os})` : ''}`;
}

/**
 * Mantém a sessão do usuário. No início, tenta renovar o access token via cookie
 * httpOnly (POST /auth/refresh) e carrega GET /auth/me.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [me, setMe] = useState<MeResponse | null>(null);

  const clearSession = useCallback(() => {
    api.setAccessToken(null);
    setMe(null);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => {
    let active = true;
    (async () => {
      const tokens = await api.refresh();
      if (!active) return;
      if (!tokens) {
        setStatus('anonymous');
        return;
      }
      try {
        const current = await authApi.me();
        if (!active) return;
        setMe(current);
        setStatus('authenticated');
      } catch {
        if (active) setStatus('anonymous');
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // Falha definitiva de renovação → sessão encerrada (RequireAuth redireciona para /login).
  useEffect(() => api.onAuthFailure(() => clearSession()), [clearSession]);

  const login = useCallback<AuthContextValue['login']>(async (input) => {
    const response = await authApi.login({
      email: input.email,
      password: input.password,
      platform: DevicePlatform.WEB,
      deviceName: browserDeviceName(),
    });
    api.setAccessToken(response.accessToken);
    setMe(response.user);
    setStatus('authenticated');
    return response.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* mesmo com erro, encerra a sessão local */
    }
    clearSession();
  }, [clearSession]);

  const reloadMe = useCallback(async () => {
    const current = await authApi.me();
    setMe(current);
  }, []);

  const can = useCallback(
    (permission: Permission) => hasPermission(me?.permissions, permission),
    [me],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      me,
      login,
      logout,
      reloadMe,
      can,
      isEmployee: !!me?.employee,
    }),
    [status, me, login, logout, reloadMe, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
